import { apiFetch, readJson } from './client';

export type SyncRunStatus = 'Running' | 'Succeeded' | 'PartialSuccess' | 'Failed' | 'Cancelled';

export interface SyncRunSummary {
  id: string;
  status: SyncRunStatus;
  startedAt: string;
  finishedAt: string | null;
  triggerKind: string | null;
  triggeredBy: string | null;
  summary: string | null;
  processed: number;
  pushed: number;
  deleted: number;
  pulled: number;
  errors: number;
}

export interface SyncJob {
  name: string;
  displayName: string;
  description: string | null;
  cronSchedule: string;
  enabled: boolean;
  settings: unknown | null;
  updatedAt: string | null;
  updatedBy: string | null;
  nextFireTimeUtc: string | null;
  lastRun: SyncRunSummary | null;
}

export interface UpdateSyncJobBody {
  cronSchedule?: string;
  enabled?: boolean;
  settings?: unknown;
  displayName?: string;
  description?: string;
}

export interface SyncRun {
  id: string;
  jobName: string;
  status: SyncRunStatus;
  triggerKind: string;
  triggeredBy: string | null;
  startedAt: string;
  finishedAt: string | null;
  summary: string | null;
  processed: number;
  pushed: number;
  deleted: number;
  pulled: number;
  errors: number;
  details: unknown | null;
}

export interface SyncRunListPage {
  total: number;
  items: SyncRun[];
}

export interface ListSyncRunsOptions {
  jobName?: string;
  status?: SyncRunStatus;
  from?: string;
  to?: string;
  skip?: number;
  take?: number;
  search?: string;
  filter?: string;
  sortBy?: string;
  sortDesc?: boolean;
}

function toQueryString(params: Record<string, string | number | boolean | undefined>): string {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return parts.length === 0 ? '' : `?${parts.join('&')}`;
}

export async function listSyncJobs(): Promise<SyncJob[]> {
  const res = await apiFetch('/api/admin/sync-jobs');
  if (!res.ok) throw new Error(`Failed to list sync jobs: ${res.status}`);
  return readJson<SyncJob[]>(res);
}

export async function getSyncJob(name: string): Promise<SyncJob> {
  const res = await apiFetch(`/api/admin/sync-jobs/${encodeURIComponent(name)}`);
  if (!res.ok) throw new Error(`Failed to load sync job: ${res.status}`);
  return readJson<SyncJob>(res);
}

export async function updateSyncJob(name: string, body: UpdateSyncJobBody): Promise<SyncJob> {
  const res = await apiFetch(`/api/admin/sync-jobs/${encodeURIComponent(name)}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
  if (res.status === 400) {
    const detail = await readJson<{ error?: string; message?: string }>(res);
    throw new Error(detail.message ?? detail.error ?? 'Invalid request');
  }
  if (!res.ok) throw new Error(`Failed to update sync job: ${res.status}`);
  return readJson<SyncJob>(res);
}

export async function triggerSyncJob(name: string): Promise<void> {
  const res = await apiFetch(`/api/admin/sync-jobs/${encodeURIComponent(name)}/trigger`, { method: 'POST' });
  if (!res.ok && res.status !== 202) throw new Error(`Failed to trigger sync job: ${res.status}`);
}

export async function listSyncRuns(opts: ListSyncRunsOptions = {}): Promise<SyncRunListPage> {
  const qs = toQueryString({ ...opts });
  const res = await apiFetch(`/api/admin/sync-runs${qs}`);
  if (!res.ok) throw new Error(`Failed to list sync runs: ${res.status}`);
  return readJson<SyncRunListPage>(res);
}

export async function getSyncRun(id: string): Promise<SyncRun> {
  const res = await apiFetch(`/api/admin/sync-runs/${encodeURIComponent(id)}`);
  if (!res.ok) throw new Error(`Failed to load sync run: ${res.status}`);
  return readJson<SyncRun>(res);
}
